import { useState } from 'react';
import { Lock, Ticket as TicketIcon } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UserCell } from '@/components/tombolo/UserCell';
import { actionLabel } from '@/lib/tombolo';
import type { TomboloState } from '@/hooks/useTombolo';

export function StepTickets({ state }: { state: TomboloState }) {
  const { previewTickets, shuffleSeed, engagement, finalizeTickets, setStep, isLocked, finalized } = state;
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!engagement) return null;

  const tickets = finalized?.tickets ?? previewTickets;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TicketIcon className="h-5 w-5 text-primary" aria-hidden />
          Tickets assigned
        </CardTitle>
        <CardDescription className="text-base">
          Each eligible person gets one ticket, numbered 1 to {tickets.length}.
          Ticket numbers are assigned by a <strong>random shuffle</strong> —
          nobody can predict or influence their number. The shuffle uses a
          public seed, so anyone can verify the order afterwards.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border bg-muted/40 p-4 text-sm space-y-1">
          <p className="font-medium">Verifiable shuffle</p>
          <p className="text-muted-foreground">
            Each person's position = SHA-256(seed + ":" + public key), sorted.
            The trust score plays no part in the order — it only decided who is eligible.
          </p>
          <p className="text-muted-foreground">
            Shuffle seed:{' '}
            <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded break-all">
              {finalized?.shuffleSeed ?? shuffleSeed}
            </code>
          </p>
        </div>

        <ScrollArea className="h-96 rounded-lg border">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead className="w-20">Ticket</TableHead>
                <TableHead>Person</TableHead>
                <TableHead className="hidden sm:table-cell">Qualified by</TableHead>
                <TableHead className="text-right w-20">Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.map((t) => (
                <TableRow key={t.participant.pubkey}>
                  <TableCell className="font-bold tabular-nums text-lg">#{t.number}</TableCell>
                  <TableCell>
                    <UserCell pubkey={t.participant.pubkey} metadata={engagement.profiles[t.participant.pubkey]} />
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant="secondary">{actionLabel(t.participant.actions)}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{t.trust.score}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>

        <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
          <Button variant="outline" onClick={() => setStep('filter')} disabled={isLocked}>
            Back
          </Button>
          {isLocked ? (
            <Button size="lg" onClick={() => setStep('draw')}>
              Go to the draw
            </Button>
          ) : (
            <Button size="lg" onClick={() => setConfirmOpen(true)} disabled={tickets.length === 0}>
              <Lock className="h-4 w-4 mr-2" aria-hidden />
              Finalize {tickets.length} tickets
            </Button>
          )}
        </div>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finalize the ticket list?</AlertDialogTitle>
            <AlertDialogDescription>
              This locks the tickets, the participant list, the filter settings
              and the note IDs. Nothing can be changed afterwards — that's what
              makes the draw fair. A timestamp will be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={finalizeTickets}>
              Yes, lock it in
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
